import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  aiPlatformGovernanceApi,
  type AIPlatformDashboard,
  type AIPlatformHealth,
  type BuiltContext,
  type InferenceResult,
  type MemoryCatalogEntry,
  type RegisteredAgent,
  type RegisteredModel,
  type RegisteredPrompt,
  type RegisteredTool,
  type RoutingPolicy,
} from '../../api/aiplatform';

type Tab = 'overview' | 'prompts' | 'models' | 'agents' | 'tools' | 'memory' | 'routing' | 'inference' | 'context' | 'health';
type Theme = 'light' | 'dark';

export function AIPlatformOperationsPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<AIPlatformDashboard | null>(null);
  const [prompts, setPrompts] = useState<RegisteredPrompt[]>([]);
  const [models, setModels] = useState<RegisteredModel[]>([]);
  const [agents, setAgents] = useState<RegisteredAgent[]>([]);
  const [tools, setTools] = useState<RegisteredTool[]>([]);
  const [memory, setMemory] = useState<MemoryCatalogEntry[]>([]);
  const [routing, setRouting] = useState<RoutingPolicy[]>([]);
  const [health, setHealth] = useState<AIPlatformHealth | null>(null);
  const [inferenceResult, setInferenceResult] = useState<InferenceResult | null>(null);
  const [builtContext, setBuiltContext] = useState<BuiltContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, pr, md, ag, tl, mem, rt, hlth] = await Promise.all([
        aiPlatformGovernanceApi.dashboard(),
        aiPlatformGovernanceApi.listPrompts(),
        aiPlatformGovernanceApi.listModels(),
        aiPlatformGovernanceApi.listAgents(),
        aiPlatformGovernanceApi.listTools(),
        aiPlatformGovernanceApi.listMemory(),
        aiPlatformGovernanceApi.listRouting(),
        aiPlatformGovernanceApi.health(),
      ]);
      setDashboard(dash);
      setPrompts(pr);
      setModels(md);
      setAgents(ag);
      setTools(tl);
      setMemory(mem);
      setRouting(rt);
      setHealth(hlth);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load AI platform');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runInference = async () => {
    setBusy(true);
    setInferenceResult(null);
    try {
      setInferenceResult(await aiPlatformGovernanceApi.runInference({
        input: 'Analyze Q3 demand plan variance and recommend safety stock adjustments',
        use_context: true, use_memory: true,
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Inference failed');
    } finally {
      setBusy(false);
    }
  };

  const loadContext = async () => {
    setBusy(true);
    try {
      setBuiltContext(await aiPlatformGovernanceApi.buildContext({
        entity: 'demand_plan', include_semantic: true, include_memory: true, include_policy: true,
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Context build failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'prompts', label: 'Prompt Registry' },
    { id: 'models', label: 'Model Registry' },
    { id: 'agents', label: 'Agent Registry' },
    { id: 'tools', label: 'Tool Registry' },
    { id: 'memory', label: 'Memory Explorer' },
    { id: 'routing', label: 'AI Routing' },
    { id: 'inference', label: 'Inference' },
    { id: 'context', label: 'Context Builder' },
    { id: 'health', label: 'AI Health' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-cyan-500">DEV-000083</p>
          <h1 className="text-2xl font-semibold">Enterprise AI Platform</h1>
          <p className={`text-sm ${muted}`}>Prompt, model & agent registry, memory platform, LLM routing & AI governance</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/semantic-governance" className="text-cyan-500 hover:underline">Semantic Layer</Link>
            <span className={muted}>·</span>
            <Link to="/platform/ai-governance" className="text-cyan-500 hover:underline">AI Governance (EGB)</Link>
            <span className={muted}>·</span>
            <Link to="/copilot" className="text-cyan-500 hover:underline">Copilot</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
          <button type="button" onClick={refresh} disabled={busy} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50">Refresh</button>
        </div>
      </div>

      {error && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-red-900 bg-red-950 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{error}</div>}

      <div className="flex flex-wrap gap-2 border-b pb-2" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-cyan-600 text-white' : dark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && health && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>AI Health Score</p><p className="mt-1 text-3xl font-semibold text-cyan-500">{health.ai_platform_health_score.toFixed(1)}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Registered Models</p><p className="mt-1 text-3xl font-semibold">{dashboard.models}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Active Agents</p><p className="mt-1 text-3xl font-semibold">{dashboard.agents}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Cost (24h)</p><p className="mt-1 text-3xl font-semibold">${health.model_cost_24h_usd.toFixed(2)}</p></div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h2 className="font-medium">Extension Points</h2>
            <ul className={`mt-2 space-y-1 text-sm ${muted}`}>
              {dashboard.extension_points.map((ep) => (
                <li key={ep.dev_id}><span className="font-mono text-cyan-500">{ep.dev_id}</span> — {ep.name}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'prompts' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Identifier</th><th className="px-4 py-2 text-left">Version</th><th className="px-4 py-2 text-left">Purpose</th><th className="px-4 py-2 text-left">Lifecycle</th><th className="px-4 py-2 text-left">Usage 24h</th></tr>
            </thead>
            <tbody>
              {prompts.map((p) => (
                <tr key={p.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                  <td className="px-4 py-2 font-mono text-xs">{p.identifier}</td>
                  <td className="px-4 py-2">{p.version}</td>
                  <td className="px-4 py-2">{p.purpose}</td>
                  <td className="px-4 py-2">{p.lifecycle}</td>
                  <td className="px-4 py-2">{p.usage_count_24h}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'models' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {models.map((m) => (
            <div key={m.id} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">{m.name}</h3>
              <p className={`text-xs ${muted}`}>{m.provider} · {m.lifecycle}</p>
              <p className={`mt-2 text-sm ${muted}`}>Context: {m.context_window.toLocaleString()} · Latency: {m.avg_latency_ms}ms</p>
              <p className={`text-xs ${muted}`}>Cost: ${m.cost_per_1k_tokens}/1K tokens</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'agents' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {agents.map((a) => (
            <div key={a.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex justify-between"><h3 className="font-medium">{a.name}</h3><span className={`text-xs ${muted}`}>{a.kind}</span></div>
              <p className={`mt-2 text-sm ${muted}`}>{a.mission}</p>
              <p className={`mt-1 text-xs ${muted}`}>Tools: {(a.tools ?? []).join(', ')}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'tools' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">Kind</th><th className="px-4 py-2 text-left">Owner</th><th className="px-4 py-2 text-left">Rate Limit</th></tr>
            </thead>
            <tbody>
              {tools.map((t) => (
                <tr key={t.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                  <td className="px-4 py-2">{t.name}</td>
                  <td className="px-4 py-2">{t.kind}</td>
                  <td className="px-4 py-2">{t.owner}</td>
                  <td className="px-4 py-2">{t.rate_limit_rps} rps</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'memory' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {memory.map((m) => (
            <div key={m.id} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">{m.name}</h3>
              <p className={`text-xs ${muted}`}>{m.kind} · {m.scope}</p>
              <p className={`mt-2 text-sm ${muted}`}>{m.entry_count.toLocaleString()} entries · {(m.size_bytes / 1_000_000).toFixed(1)} MB</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'routing' && (
        <div className="space-y-3">
          {routing.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex justify-between"><h3 className="font-medium">{r.name}</h3><span className={r.enabled ? 'text-emerald-500 text-xs' : 'text-slate-500 text-xs'}>{r.enabled ? 'Enabled' : 'Disabled'}</span></div>
              <p className={`text-sm ${muted}`}>{r.strategy}: {r.primary_model} → {r.fallback_model ?? 'none'}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'inference' && (
        <div className="space-y-4">
          <button type="button" onClick={runInference} disabled={busy} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm text-white hover:bg-cyan-700 disabled:opacity-50">Run Sample Inference</button>
          {inferenceResult && (
            <div className={`rounded-xl border p-4 ${card}`}>
              <p className="text-sm"><span className={muted}>Model:</span> {inferenceResult.model_used} ({inferenceResult.provider})</p>
              <p className="text-sm"><span className={muted}>Tokens:</span> {inferenceResult.tokens_used} · <span className={muted}>Latency:</span> {inferenceResult.latency_ms}ms · <span className={muted}>Cost:</span> ${inferenceResult.cost_usd.toFixed(4)}</p>
              <p className={`mt-2 text-sm ${muted}`}>{inferenceResult.output}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'context' && (
        <div className="space-y-4">
          <button type="button" onClick={loadContext} disabled={busy} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm text-white hover:bg-cyan-700 disabled:opacity-50">Build Business Context</button>
          {builtContext && (
            <div className={`rounded-xl border p-4 ${card} space-y-3`}>
              <p className="text-sm">Quality Score: <span className="font-semibold text-cyan-500">{builtContext.context_quality_score.toFixed(1)}</span></p>
              {builtContext.business_definitions && <div><h4 className="font-medium text-sm">Definitions</h4><ul className={`text-sm ${muted}`}>{builtContext.business_definitions.map((d) => <li key={d}>{d}</li>)}</ul></div>}
              {builtContext.kpi_context && <div><h4 className="font-medium text-sm">KPIs</h4><ul className={`text-sm ${muted}`}>{builtContext.kpi_context.map((k) => <li key={k}>{k}</li>)}</ul></div>}
            </div>
          )}
        </div>
      )}

      {tab === 'health' && health && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Token Usage (24h)</p><p className="mt-1 text-2xl font-semibold">{health.token_usage_24h.toLocaleString()}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Prompt Success</p><p className="mt-1 text-2xl font-semibold">{health.prompt_success_rate.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Avg Latency</p><p className="mt-1 text-2xl font-semibold">{health.avg_inference_latency_ms.toFixed(0)} ms</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Memory Utilization</p><p className="mt-1 text-2xl font-semibold">{health.memory_utilization_pct.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Context Quality</p><p className="mt-1 text-2xl font-semibold">{health.context_quality_avg.toFixed(1)}</p></div>
        </div>
      )}
    </div>
  );
}
